/* eslint-disable max-len */
import React, { ComponentRef, forwardRef, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { FilterAndSortField as MyNetworkClientsFilterAndSortField } from '@1po/1po-bff-fe-spec/generated/my_network/request/model/FilterAndSortField';
import { Client } from '@1po/1po-bff-fe-spec/generated/my_network/response/MyNetworkClientsResponse';
import { Table } from 'antd-v5';
import { SorterResult } from 'antd-v5/es/table/interface';
import { v4 as uuidv4 } from 'uuid';
import { ExportIcon } from 'assets/icons';
import { Pagination } from 'components/Pagination';
import { useFetchMyNetworkClients } from 'domains/r1Network/R1Network.requests';
import {
  fetchMyNetworkClientsRequestSaga,
  getMyNetworkClientsPagination,
  getMyNetworkClientsSort,
  setMyNetworkClientsSort,
  setSelectedClient,
} from 'domains/r1Network/R1Network.store';
import { MyNetworkFilter } from 'pages/MyNetworkPage/Tables/MyNetworkFilter';
import { exportMyNetworkToCSV, getMyNetworkTableColumns } from 'pages/MyNetworkPage/Tables/MyNetworkTable.utils';
import { Empty, Flex, IconButton } from 'UI';
import { getData, isLoading } from 'utils';
import { scrollToTopSmooth } from 'utils/hooks/useResetScroll';

// eslint-disable-next-line react/display-name
const MyNetworkTable = forwardRef<ComponentRef<typeof Table>>((_props, ref) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const clientsState = useFetchMyNetworkClients();
  const clientsDataStatus = clientsState.searchStatus;
  const clientsData = getData(clientsState?.data)?.clients ?? [];
  const clientsTotal = getData(clientsState?.data)?.total ?? 0;
  const pagination = useSelector(getMyNetworkClientsPagination);
  const sort = useSelector(getMyNetworkClientsSort);

  const tableKey = useMemo(
    () => uuidv4(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [clientsDataStatus],
  );

  const handleClick = useCallback(
    (client: Client) => {
      dispatch(setSelectedClient(client));
    },
    [dispatch],
  );

  const tableColumns = useMemo(() => getMyNetworkTableColumns(t, handleClick, sort), [t, handleClick, sort]);

  const handleChangePage = useCallback(
    (page: number, pageSize: number) => {
      scrollToTopSmooth();
      dispatch(fetchMyNetworkClientsRequestSaga({ page, pageSize }));
    },
    [dispatch],
  );

  const handleTableChange = useCallback(
    (_pagination: unknown, _filters: unknown, sorter: SorterResult<Client> | SorterResult<Client>[]) => {
      const singleSorter = Array.isArray(sorter) ? sorter[0] : sorter;
      const field = singleSorter?.columnKey;
      if (!field) return;
      const direction = singleSorter?.order === 'descend' ? 'DESC' : 'ASC';
      dispatch(setMyNetworkClientsSort({ field: field as MyNetworkClientsFilterAndSortField, direction }));
    },
    [dispatch],
  );

  const handleExportToCSV = () => {
    exportMyNetworkToCSV(clientsData, t);
  };

  return (
    <Flex direction={'column'}>
      <Flex padding={'0 0 16px 16px'} align={'center'} justify={'flex-end'}>
        <IconButton
          round
          boxSize={'smd'}
          hoverFill={'grey_06'}
          IconComponent={ExportIcon}
          size={24}
          onClick={handleExportToCSV}
          disabled={!clientsTotal || isLoading(clientsDataStatus)}
          tooltip={t('my_network.clients.export_csv', 'Export client list')}
        />
        <MyNetworkFilter />
      </Flex>
      <Table
        key={tableKey}
        bordered
        ref={ref}
        columns={tableColumns}
        dataSource={clientsData}
        rowKey={'uniqueId'}
        loading={isLoading(clientsDataStatus)}
        pagination={false}
        onChange={handleTableChange}
        locale={{
          triggerDesc: t('common.table.sort.descending', 'Filter in descending order'),
          triggerAsc: t('common.table.sort.ascending', 'Filter in ascending order'),
          cancelSort: t('common.table.sort.ascending', 'Filter in ascending order'),
          emptyText: <Empty description={t('common.table.empty', 'No results found')} />,
        }}
      />
      {!!clientsTotal && (
        <Pagination
          total={clientsTotal}
          pageSize={pagination.pageSize}
          currentPage={pagination.page}
          onPageChange={handleChangePage}
        />
      )}
    </Flex>
  );
});

export default MyNetworkTable;
